import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MessageCircle,
  Send,
  Instagram,
  MapPin,
  Clock,
  Users,
  Star,
  ArrowUpRight,
  Menu,
  X,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { BRAND, TOURS, GROUP_TOURS, REVIEWS, FAQ } from "@/data/tours";
import BookingDialog from "@/components/BookingDialog";
import WhatsAppFloat from "@/components/WhatsAppFloat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE =
  "https://images.pexels.com/photos/19756404/pexels-photo-19756404.jpeg?auto=compress&cs=tinysrgb&w=1600";
const ABOUT_IMAGE =
  "https://customer-assets.emergentagent.com/job_seo-tour-guide/artifacts/wnn0wov7_IMG_20241113_100008506.webp";

const NAV_LINKS = [
  { href: "#tours", label: "Экскурсии" },
  { href: "#group-tours", label: "Групповые" },
  { href: "#about", label: "О гиде" },
  { href: "#reviews", label: "Отзывы" },
  { href: "#faq", label: "Вопросы" },
  { href: "#contact", label: "Контакты" },
];

/* -------------------- HEADER -------------------- */
function Header({ onBook }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          data-testid="brand-logo"
          className={`font-display text-xl md:text-[22px] tracking-tight ${
            scrolled ? "text-foreground" : "text-white"
          }`}
        >
          <span className="font-semibold">ДЕЛИ</span>
          <span className="opacity-60 mx-2">·</span>
          <span className="italic">с Али</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className={`text-[13px] font-medium link-editorial transition ${
                scrolled ? "text-foreground/80 hover:text-foreground" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="header-phone"
            className={`hidden lg:inline-flex items-center gap-2 text-[13px] font-medium ${
              scrolled ? "text-foreground" : "text-white"
            }`}
          >
            <Phone className="h-4 w-4" />
            {BRAND.phone}
          </a>
          <button
            data-testid="header-book-btn"
            onClick={() => onBook(null)}
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#F07E26] text-white px-5 py-2.5 text-[13px] font-semibold hover:bg-[#d86a15] transition"
          >
            Забронировать
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden rounded-full p-2 border ${
              scrolled ? "border-border text-foreground" : "border-white/30 text-white"
            }`}
            aria-label="Меню"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#FAF7F2] border-t border-border">
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                className="text-[15px] font-medium text-foreground/80 hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-book-btn"
              onClick={() => {
                setOpen(false);
                onBook(null);
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#F07E26] text-white px-5 py-3 text-[14px] font-semibold"
            >
              Забронировать <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------- HERO -------------------- */
function Hero({ onBook }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <img
        src={HERO_IMAGE}
        alt="Тадж-Махал на рассвете — экскурсии в Индии с русскоговорящим гидом"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-40 pb-24 md:pt-48 md:pb-28 min-h-[100svh] flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="max-w-3xl"
        >
          <p className="overline text-[#F5C58C]" data-testid="hero-overline">
            नमस्ते · Дели, Индия · Частный гид Али
          </p>
          <h1
            data-testid="hero-h1"
            className="font-display text-[42px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[86px] text-white mt-4"
          >
            Экскурсии в Дели
            <br />
            <span className="italic text-[#F5C58C]">с русскоговорящим</span>
            <br />
            гидом
          </h1>
          <p className="mt-8 max-w-xl text-white/85 text-[15px] md:text-base leading-relaxed">
            Тадж-Махал на рассвете, Золотой треугольник, гастротуры по старому
            Дели, Варанаси и Ладакх — 24 авторских маршрута по всей Индии.
            Безопасно, на русском, с личным подходом.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => onBook(null)}
              className="inline-flex items-center gap-2 rounded-full bg-[#F07E26] text-white px-7 py-3.5 text-[14px] font-semibold hover:bg-[#d86a15] transition hover:scale-[1.02]"
            >
              Выбрать экскурсию
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <a
              href={BRAND.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-7 py-3.5 text-[14px] font-medium hover:bg-white hover:text-foreground transition"
            >
              <MessageCircle className="h-4 w-4" />
              Написать в WhatsApp
            </a>
          </div>
        </motion.div>

        <div className="hidden md:flex items-end justify-between text-white/80 mt-10">
          <div>
            <p className="overline text-white/60">Гид</p>
            <p className="font-display text-2xl mt-1">Али · 12 лет в туризме</p>
          </div>
          <div className="text-right">
            <p className="overline text-white/60">Городов в маршрутах</p>
            <p className="font-display text-2xl mt-1">14 направлений</p>
          </div>
          <div className="text-right hidden lg:block">
            <p className="overline text-white/60">Русскоязычных гостей</p>
            <p className="font-display text-2xl mt-1">2 400+</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- MARQUEE strip -------------------- */
function StripMarquee() {
  const items = [
    "Тадж-Махал", "Золотой треугольник", "Старый Дели", "Джайпур",
    "Варанаси", "Пушкар", "Ладакх", "Керала", "Гоа",
    "Ришикеш", "Джодхпур", "Удайпур", "Дхарамсала",
  ];
  return (
    <div className="bg-[#1A2035] text-[#FAF7F2] py-4 overflow-hidden border-y border-[#2A3352]">
      <div className="flex whitespace-nowrap animate-[marquee_40s_linear_infinite] gap-10">
        {[...items, ...items, ...items].map((it, i) => (
          <span key={i} className="font-display text-lg md:text-xl italic opacity-90">
            <span className="text-[#F07E26] mr-3">◆</span>
            {it}
          </span>
        ))}
      </div>
      <style>{`
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }
      `}</style>
    </div>
  );
}

/* -------------------- ABOUT -------------------- */
function AboutGuide() {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-5 relative">
          <div className="arch-frame relative aspect-[3/4] max-w-[420px]">
            <img
              src={ABOUT_IMAGE}
              alt="Али — русскоговорящий частный гид по Индии, у форта Амбер в Джайпуре"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="hidden md:block absolute -bottom-6 -right-6 bg-[#F07E26] text-white px-5 py-3 rounded-md">
            <p className="font-display text-3xl leading-none">12+</p>
            <p className="overline text-white/90 mt-1">лет с русскими гостями</p>
          </div>
        </div>

        <div className="md:col-span-7">
          <p className="overline text-[#D0533C]" data-testid="about-overline">О гиде</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            <span className="italic">«Откройте настоящую</span>
            <br />
            Индию со мной»
          </h2>

          <div className="mt-8 space-y-5 text-foreground/80 leading-relaxed max-w-xl">
            <p>
              Меня зовут <b className="text-foreground">Али</b>. Я родился в Агре, а живу в Дели, изучал русский
              язык в Университете Агры и уже более двенадцати лет сопровождаю
              путешественников из разных стран мира.
            </p>
            <p>
              Моя миссия — показать вам ту Индию, которую невозможно увидеть с
              группой из автобуса: живые рынки Чандни-Чоук, ужин в семье
              местных, рассвет над Гангом и Тадж-Махал в свете первых лучей.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-border pt-8">
            <Stat label="Довольных гостей" value="2 400+" />
            <Stat label="Городов Индии" value="14" />
            <Stat label="Языков" value="RU · EN · HI" />
            <Stat label="Средняя оценка" value="4.98 / 5" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-2xl md:text-3xl">{value}</p>
      <p className="overline text-muted-foreground mt-2">{label}</p>
    </div>
  );
}

/* -------------------- TOUR CATALOG -------------------- */
// Категории экскурсий — сгруппированы по темам для удобной навигации и SEO
const CATEGORIES = [
  {
    id: "delhi",
    label: "Дели",
    title: "Экскурсии по Дели",
    subtitle: "Столица Индии — от Красного форта до Кутб-Минара",
    ids: [
      "delhi-first-look",
      "old-delhi-spices",
      "mughal-heritage",
      "lotus-akshardham",
      "akshardham-fountain-show",
      "sikh-temples",
      "delhi-street-food",
      "delhi-night",
      "delhi-transit",
    ],
  },
  {
    id: "taj-agra",
    label: "Тадж-Махал и Агра",
    title: "Тадж-Махал и Агра",
    subtitle: "Столица Великих Моголов и «слеза на щеке вечности»",
    ids: [
      "taj-sunrise",
      "airport-to-taj",
      "taj-friday-yamuna",
      "agra-dream-train",
      "agra-fatehpur",
      "living-agra-crafts",
    ],
  },
  {
    id: "rajasthan",
    label: "Джайпур и Раджастан",
    title: "Джайпур и Раджастан",
    subtitle: "Розовый, синий и белый города магарадж",
    ids: ["pink-jaipur", "udaipur", "jodhpur"],
  },
  {
    id: "spiritual",
    label: "Духовные маршруты",
    title: "Духовная Индия",
    subtitle: "Ганга, ашрамы и мистика древнейших городов мира",
    ids: ["varanasi-ganges", "rishikesh-haridwar"],
  },
  {
    id: "experiences",
    label: "Гастро · Фото · Опыты",
    title: "Гастро, фото и авторские опыты",
    subtitle: "То, что нельзя увидеть с автобусной группой",
    ids: ["cooking-class", "sari-photoshoot"],
  },
  {
    id: "multi-day",
    label: "Многодневные и комбо",
    title: "Многодневные и индивидуальные туры",
    subtitle: "Классические маршруты и авторские программы под вас",
    ids: ["golden-triangle-3d", "goa-heritage", "custom-tour"],
  },
];

function TourCatalog({ onBook }) {
  const byId = useMemo(() => Object.fromEntries(TOURS.map((t) => [t.id, t])), []);
  const [active, setActive] = useState(CATEGORIES[0].id);

  const scrollToCat = (id) => {
    setActive(id);
    const el = document.getElementById(`cat-${id}`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section id="tours" data-testid="tours-section" className="py-24 md:py-32 bg-paper">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <p className="overline text-[#D0533C]">Каталог экскурсий</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            <span className="italic">21 авторский</span> маршрут
            <br />
            по Дели и Индии
          </h2>
          <p className="mt-6 text-foreground/70 max-w-2xl">
            Все экскурсии сгруппированы по темам — от классического Дели до
            фестивалей и духовных путешествий. Русскоговорящий гид Али сопровождает лично.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 md:gap-3" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`category-${c.id}`}
              onClick={() => scrollToCat(c.id)}
              className={`px-4 py-2 rounded-full text-[12px] font-medium border transition ${
                active === c.id
                  ? "bg-[#1A2035] text-white border-[#1A2035]"
                  : "border-border text-foreground/70 hover:border-[#1A2035]"
              }`}
            >
              {c.label} <span className="opacity-60 ml-1">· {c.ids.length}</span>
            </button>
          ))}
        </div>

        <div className="mt-16 space-y-24">
          {CATEGORIES.map((cat) => {
            const items = cat.ids.map((id) => byId[id]).filter(Boolean);
            if (!items.length) return null;
            return (
              <div key={cat.id} id={`cat-${cat.id}`} data-testid={`cat-section-${cat.id}`} className="scroll-mt-28">
                <div className="flex items-end justify-between gap-6 border-b border-border pb-6">
                  <div>
                    <p className="overline text-[#D0533C]">{cat.label}</p>
                    <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
                      {cat.title}
                    </h3>
                    <p className="mt-3 text-foreground/70 max-w-2xl text-[14px]">{cat.subtitle}</p>
                  </div>
                  <span className="hidden md:inline overline text-muted-foreground pb-2">
                    {items.length} {items.length === 1 ? "тур" : "туров"}
                  </span>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                  {items.map((t, i) => (
                    <TourCard key={t.id} tour={t} index={i} onBook={onBook} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TourCard({ tour, index, onBook }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      data-testid={`tour-card-${tour.id}`}
      className="group flex flex-col"
    >
      <Link to={`/tours/${tour.id}`} data-testid={`tour-link-${tour.id}`} className="block relative overflow-hidden rounded-md aspect-[4/5] bg-muted">
        <img
          src={tour.image}
          alt={tour.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute top-4 left-4 bg-[#FAF7F2]/95 backdrop-blur px-3 py-1.5 rounded-full">
          <span className="overline text-foreground/80">{tour.city}</span>
        </div>
      </Link>

      <div className="mt-5 flex-1 flex flex-col">
        <div className="flex items-center gap-5 text-[12px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {tour.duration}</span>
          <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {tour.group}</span>
        </div>
        <Link to={`/tours/${tour.id}`} className="mt-3 block">
          <h3 className="font-display text-2xl md:text-[26px] leading-tight hover:text-[#D0533C] transition-colors">
            {tour.title}
          </h3>
        </Link>
        <p className="mt-3 text-foreground/70 text-[14px] leading-relaxed">{tour.short}</p>

        <div className="mt-6 pt-5 border-t border-border flex items-center justify-between gap-3">
          <div>
            <p className="overline text-muted-foreground">Цена</p>
            <p className="font-display text-xl mt-1">{tour.price}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/tours/${tour.id}`}
              data-testid={`tour-details-${tour.id}`}
              className="hidden sm:inline-flex text-[13px] font-medium text-foreground/70 link-editorial"
            >
              Подробнее
            </Link>
            <button
              data-testid={`tour-book-${tour.id}`}
              onClick={() => onBook(tour)}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A2035] link-editorial"
            >
              Забронировать
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* -------------------- GROUP TOURS -------------------- */
function GroupTours({ onBook }) {
  return (
    <section id="group-tours" data-testid="group-tours-section" className="py-24 md:py-32 bg-[#EBE5D9]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="overline text-[#D0533C]">Групповые туры с фиксированными датами</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Едем <span className="italic">вместе</span> — на праздники
              <br />
              и еженедельно
            </h2>
            <p className="mt-6 text-foreground/70 max-w-xl">
              Присоединяйтесь к сборным группам Али: Дивали, Холи, Новый год в Индии
              и еженедельный тур «Индия для искателей». Приватный уровень сервиса,
              стоимость групповой поездки.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {GROUP_TOURS.map((t, i) => (
            <motion.article
              key={t.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              data-testid={`group-tour-card-${t.id}`}
              className="group flex flex-col bg-white rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <Link to={`/tours/${t.id}`} data-testid={`group-tour-link-${t.id}`} className="relative block aspect-[5/4] bg-muted overflow-hidden">
                <img
                  src={t.image}
                  alt={t.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 flex flex-col gap-1.5" data-testid={`group-tour-badges-${t.id}`}>
                  <span
                    className="bg-[#1A2035] text-[#F5C58C] px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold w-fit"
                    data-testid={`group-tour-format-${t.id}`}
                  >
                    Формат групповой
                  </span>
                  <span
                    className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold w-fit ${
                      t.frequency === "weekly"
                        ? "bg-[#2E7D5B] text-white"
                        : "bg-[#D0533C] text-white"
                    }`}
                    data-testid={`group-tour-frequency-${t.id}`}
                  >
                    {t.frequency === "weekly" ? "Еженедельно" : "Едем вместе — на праздники"}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 bg-[#FAF7F2]/95 backdrop-blur px-3 py-2 rounded-md">
                  <p className="overline text-[#D0533C]">Ближайшие даты</p>
                  <p className="font-display text-lg mt-0.5">{t.dates}</p>
                </div>
              </Link>

              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-5 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t.duration}</span>
                  <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {t.group}</span>
                </div>
                <Link to={`/tours/${t.id}`}>
                  <h3 className="font-display text-2xl md:text-[26px] leading-tight mt-3 hover:text-[#D0533C] transition-colors">
                    {t.title}
                  </h3>
                </Link>
                <p className="mt-3 text-foreground/70 text-[14px] leading-relaxed">{t.short}</p>
                <p className="mt-4 text-[12px] text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                  {t.city}
                </p>

                <div className="mt-6 pt-5 border-t border-border flex items-center justify-between gap-3">
                  <div>
                    <p className="overline text-muted-foreground">Стоимость</p>
                    <p className="font-display text-xl mt-1">{t.price}</p>
                  </div>
                  <button
                    data-testid={`group-tour-book-${t.id}`}
                    onClick={() => onBook(t)}
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A2035] link-editorial"
                  >
                    Забронировать
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- REVIEWS -------------------- */
function Reviews() {
  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 bg-[#1A2035] text-[#FAF7F2]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl">
          <p className="overline text-[#F5C58C]">Отзывы гостей</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            <span className="italic">4.98 из 5</span> — так оценивают
            <br />
            наши поездки
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              data-testid={`review-${i}`}
              className="rounded-lg border border-[#2A3352] p-7 bg-[#141a2c]"
            >
              <div className="flex gap-1 text-[#F07E26] mb-4">
                {Array.from({ length: r.stars }).map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="font-display italic text-xl leading-relaxed text-[#FAF7F2]">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[#2A3352] text-sm">
                <span className="font-semibold">{r.name}</span>
                <span className="text-[#FAF7F2]/60 ml-2">· {r.city}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- FAQ -------------------- */
function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="overline text-[#D0533C]">Частые вопросы</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">
            Что нужно знать
            <br />
            <span className="italic">перед поездкой</span>
          </h2>
          <p className="mt-6 text-foreground/70 max-w-sm">
            Не нашли ответ? Напишите Али в WhatsApp — он отвечает в течение
            1–2 часов.
          </p>
        </div>
        <div className="md:col-span-8">
          {FAQ.map((item, i) => (
            <div key={i} className="border-b border-border" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between py-6 text-left"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-display text-xl md:text-2xl pr-6">{item.q}</span>
                <span className={`text-[#F07E26] text-3xl leading-none transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {open === i && (
                <p className="pb-6 text-foreground/70 leading-relaxed max-w-2xl">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- BOOKING CTA -------------------- */
function BookingCta({ onBook }) {
  return (
    <section id="book" data-testid="booking-cta-section" className="py-24 md:py-28 bg-[#EBE5D9]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <p className="overline text-[#D0533C]">Забронировать</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Оставьте заявку —
            <br />
            <span className="italic">Али ответит в течение часа</span>
          </h2>
          <p className="mt-6 max-w-xl text-foreground/70">
            Мы согласуем дату, программу и стоимость. Никакой предоплаты для
            однодневных экскурсий — оплата на месте после встречи.
          </p>
          <ul className="mt-6 space-y-2 text-[15px] text-foreground/80">
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F07E26]" /> Ответ в WhatsApp / Telegram за 1–2 часа</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F07E26]" /> Отмена без штрафа за 24 часа</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-[#F07E26]" /> Всё сопровождение на русском языке</li>
          </ul>
        </div>
        <div className="md:col-span-5">
          <button
            data-testid="cta-book-open"
            onClick={() => onBook(null)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A2035] text-white px-7 py-4 text-[14px] font-semibold hover:bg-[#0f142a] transition"
          >
            Оставить заявку
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <a
            href={BRAND.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-foreground/20 text-foreground px-7 py-4 text-[14px] font-medium hover:bg-white transition"
          >
            <MessageCircle className="h-4 w-4" /> Написать в WhatsApp
          </a>
          <a
            href={BRAND.telegramHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-telegram"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-foreground/20 text-foreground px-7 py-4 text-[14px] font-medium hover:bg-white transition"
          >
            <Send className="h-4 w-4" /> Написать в Telegram
          </a>
        </div>
      </div>
    </section>
  );
}

/* -------------------- FOOTER -------------------- */
function Footer() {
  return (
    <footer id="contact" data-testid="footer-section" className="bg-[#1A2035] text-[#FAF7F2] pt-24 pb-10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <p className="font-display text-3xl md:text-4xl leading-tight">
            <span className="font-semibold">ДЕЛИ</span>
            <span className="opacity-60 mx-2">·</span>
            <span className="italic">с Али</span>
          </p>
          <p className="mt-5 text-[#FAF7F2]/70 max-w-md leading-relaxed">
            Частные экскурсии по Индии с русскоговорящим гидом. Дели, Агра,
            Джайпур, Варанаси, Ришикеш, Ладакх и вся Индия — с 2012 года.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[#F5C58C]">Контакты</p>
          <ul className="mt-5 space-y-3">
            <li>
              <a href={BRAND.phoneHref} data-testid="footer-phone" className="inline-flex items-center gap-2 hover:text-[#F07E26]">
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
            </li>
            <li>
              <a href={BRAND.emailHref} data-testid="footer-email" className="inline-flex items-center gap-2 hover:text-[#F07E26] break-all">
                <Mail className="h-4 w-4" /> {BRAND.email}
              </a>
            </li>
            <li className="inline-flex items-center gap-2 text-[#FAF7F2]/80">
              <MapPin className="h-4 w-4" /> Нью-Дели, Индия
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="overline text-[#F5C58C]">Мы в соцсетях</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={BRAND.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-4 py-2 hover:bg-[#F07E26] hover:border-[#F07E26]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={BRAND.telegramHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-telegram"
              className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-4 py-2 hover:bg-[#F07E26] hover:border-[#F07E26]"
            >
              <Send className="h-4 w-4" /> Telegram
            </a>
            <a
              href={BRAND.instagramHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-4 py-2 hover:bg-[#F07E26] hover:border-[#F07E26]"
            >
              <Instagram className="h-4 w-4" /> Instagram
            </a>
            <a
              href={BRAND.vkHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-vk"
              className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-4 py-2 hover:bg-[#F07E26] hover:border-[#F07E26]"
            >
              VK
            </a>
          </div>
          <p className="mt-6 text-[12px] text-[#FAF7F2]/50 leading-relaxed">
            Работаем каждый день, отвечаем в мессенджерах с 07:00 до 23:00 по IST
            (04:30–20:30 МСК).
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 mt-16 pt-6 border-t border-[#2A3352] flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-[#FAF7F2]/60">
        <p>© {new Date().getFullYear()} Дели с Али. Все права защищены.</p>
        <p>Экскурсии в Дели · Тадж-Махал · Золотой треугольник · Русскоговорящий гид</p>
      </div>
    </footer>
  );
}

/* -------------------- PAGE -------------------- */
export default function Home() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);

  // Restore homepage SEO meta (when navigating back from /tours/:slug)
  useEffect(() => {
    const title =
      "Экскурсии в Дели и по Индии с русскоговорящим гидом — Дели с Али";
    const description =
      "Частные экскурсии в Дели, Агре, Джайпуре, Варанаси и по всей Индии с русскоговорящим гидом Али. Тадж-Махал на рассвете, Золотой треугольник, гастротуры, фотосессии в сари. Бронирование онлайн.";
    document.title = title;
    const setM = (name, content, attr = "name") => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setM("description", description);
    setM("og:title", title, "property");
    setM("og:description", description, "property");
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    setM("og:url", `${siteUrl}/`, "property");
    setM("og:type", "website", "property");
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", `${siteUrl}/`);
    // Remove tour-specific JSON-LD if present
    const t = document.getElementById("jsonld-tour");
    if (t) t.remove();
  }, []);

  const openBook = (tour) => {
    setSelectedTour(tour);
    setBookingOpen(true);
  };

  const handleSubmit = async (form) => {
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      date: form.date,
      people: Number(form.people),
      tour_id: form.tour_id,
      tour_title: form.tour_title,
      comment: form.comment?.trim() || "",
    };
    const { data } = await axios.post(`${API}/bookings`, payload, { timeout: 20000 });
    return data;
  };

  return (
    <>
      <Header onBook={openBook} />
      <Hero onBook={openBook} />
      <StripMarquee />
      <AboutGuide />
      <TourCatalog onBook={openBook} />
      <GroupTours onBook={openBook} />
      <Reviews />
      <FaqSection />
      <BookingCta onBook={openBook} />
      <Footer />
      <WhatsAppFloat />
      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        tour={selectedTour}
        tours={[...TOURS, ...GROUP_TOURS]}
        onSubmit={handleSubmit}
        onSuccess={() => toast.success("Заявка отправлена — Али свяжется с вами в течение 1–2 часов")}
        onError={(msg) => toast.error(msg || "Не удалось отправить заявку. Попробуйте ещё раз.")}
      />
    </>
  );
}
